import {Alert, Tab, TabProps, Tabs} from "@mui/material";
import {FormattedMessage} from "react-intl";
import TabPanel from "./TabPanel";
import Linked from "./linked/Linked";
import Loading from "../../breadcrumbs/Loading";
import Tooltip from "./Tooltip";
import ThirdParty from "./third-party/ThirdParty";
import Client from "./client/Client";
import Provisioned from "./provisioned/Provisioned";
import {useNavigate, useLocation} from "react-router";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useInterval} from "@dvrt/graphql-widgets";
import {
    useClientDevicesQuery,
    useDeviceLimitQuery,
    useDomainIdViaExtensionLazyQuery,
    useLinkedDevicesQuery,
    useProvisionedDevicesQuery,
    useThirdPartyDevicesQuery,
} from "./queries.generated";
import {DeviceLimitInfo, DeviceSetLimitSummary, DevicesUsedInfo, getDeviceSetLimitSummary, DeviceType} from "./utils";

const REFETCH_INTERVAL = 30_000 as const;

type Props = {
    extensionId: number;
    domainId: number;
};

const getHideInfo = (info: Record<DeviceType, DeviceLimitInfo>): Record<DeviceType, boolean> => {
    return {
        provisioned: info.provisioned.used === 0 && info.provisioned.remaining === 0,
        "client-mobile": info["client-mobile"].used === 0 && info["client-mobile"].remaining === 0,
        "client-desktop": info["client-desktop"].used === 0 && info["client-desktop"].remaining === 0,
        thirdparty: info.thirdparty.used === 0 && info.thirdparty.remaining === 0,
        linked: info.linked.used === 0,
    };
};

const canAddDevice = (info: DeviceLimitInfo): boolean => {
    return info.remaining !== 0;
};

function tabProps(key: string): TabProps {
    return {
        id: `simple-tab-${key.toLowerCase()}-devices`,
        "aria-controls": `simple-tabpanel-${key.toLowerCase()}-devices`,
        style: {
            borderBottom: `2px solid #ddd`,
        },
    };
}

const tabNames = ["provisioned", "client", "thirdParty", "linked"];

function useDataSource(userId: number) {
    const limitQuery = useDeviceLimitQuery({
        variables: {userId},
        fetchPolicy: "no-cache",
    });

    const thirdPartyQuery = useThirdPartyDevicesQuery({
        variables: {userId},
        fetchPolicy: "no-cache",
    });

    const linkedQuery = useLinkedDevicesQuery({
        variables: {userId},
        fetchPolicy: "no-cache",
    });

    const clientQuery = useClientDevicesQuery({
        variables: {userId},
        fetchPolicy: "no-cache",
    });

    const provisionedQuery = useProvisionedDevicesQuery({
        variables: {userId},
        fetchPolicy: "no-cache",
    });

    const devicesLoading =
        thirdPartyQuery.loading || linkedQuery.loading || clientQuery.loading || provisionedQuery.loading;

    const refetchQueries = useCallback(() => {
        if (devicesLoading) return;

        thirdPartyQuery.refetch();
        linkedQuery.refetch();
        clientQuery.refetch();
        provisionedQuery.refetch();
    }, [devicesLoading, thirdPartyQuery, linkedQuery, clientQuery, provisionedQuery]);

    useInterval(refetchQueries, REFETCH_INTERVAL);

    const devicesInfo: DeviceSetLimitSummary = useMemo(() => {
        const deviceLimits = limitQuery.data?.deviceLimit;
        console.debug("Device limits", deviceLimits);

        if (deviceLimits == null)
            return {
                perDevice: {
                    provisioned: {limit: undefined, used: 0, remaining: 0} as DeviceLimitInfo,
                    "client-mobile": {limit: undefined, used: 0, remaining: 0} as DeviceLimitInfo,
                    "client-desktop": {limit: undefined, used: 0, remaining: 0} as DeviceLimitInfo,
                    thirdparty: {limit: undefined, used: 0, remaining: 0} as DeviceLimitInfo,
                    linked: {limit: undefined, used: 0, remaining: 0} as DeviceLimitInfo,
                },
                remainingLimits: [],
            };

        const usedInfo: DevicesUsedInfo = {
            provisioned: provisionedQuery.data?.provisionedDevices.length ?? 0,
            "client-mobile": clientQuery.data?.clientDevices.filter((device) => device.type === "MOBILE").length ?? 0,
            "client-desktop": clientQuery.data?.clientDevices.filter((device) => device.type === "DESKTOP").length ?? 0,
            thirdparty: thirdPartyQuery.data?.thirdPartyDevices.length ?? 0,
            linked: linkedQuery.data?.linkedDevices.length ?? 0,
        };

        const info = getDeviceSetLimitSummary(
            deviceLimits.map((l) => ({
                devices: l.devices as DeviceType[],
                limit: l?.limit,
            })),
            usedInfo,
        );

        return info;
    }, [limitQuery.data, provisionedQuery.data, clientQuery.data, thirdPartyQuery.data, linkedQuery.data]);

    return {
        limitQuery,
        thirdPartyQuery,
        linkedQuery,
        clientQuery,
        provisionedQuery,
        refetchQueries,
        devicesInfo,
        devicesLoading,
        anyLoading: limitQuery.loading || devicesLoading,
    };
}

const Devices = ({extensionId, domainId}: Props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const value = Math.max(0, tabNames.indexOf(location.hash.replace("#", "")));

    const dataSource = useDataSource(extensionId);

    if (dataSource.anyLoading) return <Loading />;

    const tabs = [];
    const tabPanels = [];

    let key = "";
    let index = -1;

    const hideInfo = getHideInfo(dataSource.devicesInfo.perDevice);

    if (
        hideInfo.provisioned &&
        hideInfo["client-mobile"] &&
        hideInfo["client-desktop"] &&
        hideInfo.thirdparty &&
        hideInfo.linked
    ) {
        return (
            <Alert severity="warning">
                <FormattedMessage
                    id="no_devices_and_no_quota"
                    defaultMessage="User has no devices and no quota to add one."
                />
            </Alert>
        );
    }

    if (!hideInfo.provisioned) {
        index++;
        key = "provisioned";
        tabs.push(
            <Tab
                key={key}
                label={
                    <span>
                        <FormattedMessage id="devices.provisioned.label" defaultMessage="Provisioned devices" /> (
                        {dataSource.devicesInfo.perDevice.provisioned.used})
                    </span>
                }
                {...tabProps(key)}
            />,
        );
        tabPanels.push(
            <TabPanel key={key} index={index} value={value}>
                <Provisioned
                    domainId={domainId}
                    canAdd={canAddDevice(dataSource.devicesInfo.perDevice.provisioned)}
                    query={dataSource.provisionedQuery}
                    onMutate={dataSource.limitQuery.refetch}
                    extensionId={extensionId}
                />
            </TabPanel>,
        );
    }

    if (!hideInfo["client-mobile"] || !hideInfo["client-desktop"]) {
        index++;
        key = "client";
        tabs.push(
            <Tab
                key={key}
                label={
                    <span>
                        <FormattedMessage id="devices.client.label" defaultMessage="Clients" /> (
                        {dataSource.devicesInfo.perDevice["client-mobile"].used +
                            dataSource.devicesInfo.perDevice["client-desktop"].used}
                        )
                    </span>
                }
                {...tabProps(key)}
            />,
        );
        tabPanels.push(
            <TabPanel key={key} index={index} value={value}>
                <Client query={dataSource.clientQuery} onMutate={dataSource.limitQuery.refetch} />
            </TabPanel>,
        );
    }

    if (!hideInfo.thirdparty) {
        index++;
        key = "thirdParty";
        tabs.push(
            <Tab
                key={key}
                label={
                    <span>
                        <FormattedMessage id="devices.third_party.label" defaultMessage="Third-party devices" /> (
                        {dataSource.devicesInfo.perDevice.thirdparty.used})
                    </span>
                }
                {...tabProps(key)}
            />,
        );
        tabPanels.push(
            <TabPanel key={key} index={index} value={value}>
                <ThirdParty
                    canAdd={canAddDevice(dataSource.devicesInfo.perDevice.thirdparty)}
                    query={dataSource.thirdPartyQuery}
                    onMutate={dataSource.limitQuery.refetch}
                    extensionId={extensionId}
                    domainId={domainId}
                />
            </TabPanel>,
        );
    }

    if (!hideInfo.linked) {
        index++;
        key = "linked";
        tabs.push(
            <Tab
                key={key}
                label={
                    <span>
                        <FormattedMessage id="devices.linked.label" defaultMessage="Linked users" /> (
                        {dataSource.devicesInfo.perDevice.linked.used})
                    </span>
                }
                {...tabProps(key)}
            />,
        );
        tabPanels.push(
            <TabPanel key={key} index={index} value={value}>
                <Linked query={dataSource.linkedQuery} />
            </TabPanel>,
        );
    }

    return (
        <div style={{padding: 20}}>
            <Tabs
                indicatorColor="primary"
                value={value}
                onChange={(_, newValue) => navigate("#" + tabNames[newValue])}
                variant="fullWidth"
                id="tabs-extension-devices"
            >
                {tabs}
                <Tooltip deviceSetLimitSummary={dataSource.devicesInfo} />
            </Tabs>
            {tabPanels}
        </div>
    );
};

const PropsEnsurer = ({extensionId, domainId: _domainId}: Props) => {
    const domainId = useRef(_domainId);
    const [loading, setLoading] = useState(false);
    const [domainIdQuery] = useDomainIdViaExtensionLazyQuery({
        variables: {extensionId},
        fetchPolicy: "no-cache",
    });

    useEffect(() => {
        if (domainId.current === undefined) {
            const fetch = async () => {
                setLoading(true);
                const {data} = await domainIdQuery();
                domainId.current = data.users.items[0].number.domainId;
                setLoading(false);
            };
            fetch();
        }
    }, [domainIdQuery]);

    if (domainId === undefined || loading) {
        return <Loading />;
    }

    return <Devices extensionId={extensionId} domainId={domainId.current} />;
};

export {PropsEnsurer as Devices};
